import { isNative } from './platform';

export const META_PIXEL_ID =
  process.env.REACT_APP_META_PIXEL_ID ?? '1731741511435681';

export interface MetaAttribution {
  fbc?: string;
  fbp?: string;
  sourceUrl?: string;
}

const META_SCRIPT_ID = 'insight-meta-pixel';
const META_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
let initialized = false;
let lastPageView: { pathname: string; trackedAt: number } | null = null;

const readCookie = (name: string): string | undefined => {
  const prefix = `${name}=`;
  const value = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix))
    ?.slice(prefix.length);
  try {
    return value ? decodeURIComponent(value) : undefined;
  } catch {
    return undefined;
  }
};

const setCrossSubdomainCookie = (name: string, value: string): void => {
  const host = window.location.hostname;
  const onInsightDomain =
    host === 'insightanalytics.io' || host.endsWith('.insightanalytics.io');
  const domain = onInsightDomain ? '; domain=.insightanalytics.io' : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${META_COOKIE_MAX_AGE_SECONDS}; samesite=lax${domain}${secure}`;
};

const captureMetaClickId = (): void => {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (!fbclid || fbclid.length > 1000) return;
  const existing = readCookie('_fbc');
  const match = existing?.match(/^fb\.\d+\.\d{13}\.(.+)$/);
  if (match?.[1] === fbclid) return;
  setCrossSubdomainCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`);
};

const installQueue = (): void => {
  if (window.fbq) return;
  const queue = ((...args: unknown[]) => {
    if (queue.callMethod) {
      queue.callMethod(...args);
      return;
    }
    queue.queue.push(args);
  }) as MetaPixelFunction;
  queue.push = queue;
  queue.loaded = true;
  queue.version = '2.0';
  queue.queue = [];
  window.fbq = queue;
  window._fbq = queue;
};

export const initializeMetaPixel = (): boolean => {
  if (isNative() || process.env.REACT_APP_INSIGHT_ENV !== 'production')
    return false;
  captureMetaClickId();
  installQueue();
  if (initialized) return true;

  // The SDK hooks pushState and would fire PageView on every in-app route;
  // only trackMetaPageView's allow-listed routes may report.
  if (window.fbq) window.fbq.disablePushState = true;
  window.fbq?.('init', META_PIXEL_ID);
  initialized = true;

  if (!document.getElementById(META_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = META_SCRIPT_ID;
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }
  return true;
};

export const getMetaAttribution = (): MetaAttribution => {
  if (isNative() || process.env.REACT_APP_INSIGHT_ENV !== 'production')
    return {};
  captureMetaClickId();
  return {
    fbc: readCookie('_fbc'),
    fbp: readCookie('_fbp'),
    sourceUrl: `${window.location.origin}${window.location.pathname}`,
  };
};

export const trackMetaPageView = (pathname: string): void => {
  if (!initializeMetaPixel()) return;
  const now = Date.now();
  if (
    lastPageView?.pathname === pathname &&
    now - lastPageView.trackedAt < 1000
  ) {
    return;
  }
  window.fbq?.('track', 'PageView');
  lastPageView = { pathname, trackedAt: now };
};
